import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";

import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ModuleGuard from "@/components/ModuleGuard";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import TalentsPage from "@/pages/TalentsPage";
import TalentDetailPage from "@/pages/TalentDetailPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ContactPage from "@/pages/ContactPage";
import ProposalPage from "@/pages/ProposalPage";
import ProducerPortal from "@/pages/ProducerPortal";
import PublicInvoicePage from "@/pages/PublicInvoicePage";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAccount from "@/pages/admin/AdminAccount";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAppearance from "@/pages/admin/AdminAppearance";
import AdminMenuConfig from "@/pages/admin/AdminMenuConfig";
import AdminThemesPage from "@/pages/admin/AdminThemesPage";
import AdminTalentWorks from "@/pages/admin/AdminTalentWorks";
import AdminProposals from "@/pages/admin/AdminProposals";
import AdminProposalEditor from "@/pages/admin/AdminProposalEditor";
import CommercialDashboard from "@/pages/admin/commercial/CommercialDashboard";
import ProgramsPage from "@/pages/admin/commercial/ProgramsPage";
import PackagesPage from "@/pages/admin/commercial/PackagesPage";
import MonthlyReportsPage from "@/pages/admin/commercial/MonthlyReportsPage";
import WorkflowPage from "@/pages/admin/commercial/WorkflowPage";
import CampaignsPage from "@/pages/admin/commercial/CampaignsPage";
import InvoicesPage from "@/pages/admin/commercial/InvoicesPage";
import AccountsReceivablePage from "@/pages/admin/commercial/AccountsReceivablePage";
import { ComClientsPage, ComBrandsPage, ComAgenciesPage, ComContactsPage, ComCategoriesPage, ComProductsPage, ComTalentsPage } from "@/pages/admin/commercial/ComEntities";
import { AdminServices, AdminTalents, AdminProducts, AdminClients, AdminAbout } from "@/pages/admin/AdminEntities";

function PublicRoute({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
            <Toaster richColors position="top-right" />
            <Routes>
              <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
              <Route path="/quienes-somos" element={<PublicRoute><AboutPage /></PublicRoute>} />
              <Route path="/servicios" element={<PublicRoute><ServicesPage /></PublicRoute>} />
              <Route path="/talentos" element={<PublicRoute><TalentsPage /></PublicRoute>} />
              <Route path="/talentos/:id" element={<PublicRoute><TalentDetailPage /></PublicRoute>} />
              <Route path="/productos" element={<PublicRoute><ProductsPage /></PublicRoute>} />
              <Route path="/productos/:id" element={<PublicRoute><ProductDetailPage /></PublicRoute>} />
              <Route path="/contacto" element={<PublicRoute><ContactPage /></PublicRoute>} />
              <Route path="/propuesta/:slug" element={<ProposalPage />} />
              <Route path="/produccion/:slug" element={<ProducerPortal />} />
              <Route path="/factura-publica/:id" element={<PublicInvoicePage />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="settings" element={<ModuleGuard module="settings"><AdminSettings /></ModuleGuard>} />
                <Route path="home" element={<ModuleGuard module="settings"><AdminSettings /></ModuleGuard>} />
                <Route path="about" element={<ModuleGuard module="about"><AdminAbout /></ModuleGuard>} />
                <Route path="services" element={<ModuleGuard module="services"><AdminServices /></ModuleGuard>} />
                <Route path="talents" element={<ModuleGuard module="talents"><AdminTalents /></ModuleGuard>} />
                <Route path="talents/:id/works" element={<ModuleGuard module="talents"><AdminTalentWorks /></ModuleGuard>} />
                <Route path="products" element={<ModuleGuard module="products"><AdminProducts /></ModuleGuard>} />
                <Route path="clients" element={<ModuleGuard module="clients"><AdminClients /></ModuleGuard>} />
                <Route path="proposals" element={<ModuleGuard module="proposals"><AdminProposals /></ModuleGuard>} />
                <Route path="proposals/:id" element={<ModuleGuard module="proposals"><AdminProposalEditor /></ModuleGuard>} />
                <Route path="comercial" element={<ModuleGuard module="comercial"><CommercialDashboard /></ModuleGuard>} />
                <Route path="comercial/clientes" element={<ModuleGuard module="com_clients"><ComClientsPage /></ModuleGuard>} />
                <Route path="comercial/marcas" element={<ModuleGuard module="com_brands"><ComBrandsPage /></ModuleGuard>} />
                <Route path="comercial/agencias" element={<ModuleGuard module="com_agencies"><ComAgenciesPage /></ModuleGuard>} />
                <Route path="comercial/contactos" element={<ModuleGuard module="com_contacts"><ComContactsPage /></ModuleGuard>} />
                <Route path="comercial/categorias" element={<ModuleGuard module="com_categories"><ComCategoriesPage /></ModuleGuard>} />
                <Route path="comercial/productos-com" element={<ModuleGuard module="com_products"><ComProductsPage /></ModuleGuard>} />
                <Route path="comercial/paquetes" element={<ModuleGuard module="com_packages"><PackagesPage /></ModuleGuard>} />
                <Route path="comercial/talentos" element={<ModuleGuard module="com_talents"><ComTalentsPage /></ModuleGuard>} />
                <Route path="comercial/programas" element={<ModuleGuard module="programs"><ProgramsPage /></ModuleGuard>} />
                <Route path="comercial/workflow" element={<ModuleGuard module="com_workflow"><WorkflowPage /></ModuleGuard>} />
                <Route path="comercial/campanas" element={<ModuleGuard module="com_campaigns"><CampaignsPage /></ModuleGuard>} />
                <Route path="comercial/cobranza" element={<ModuleGuard module="com_invoices"><InvoicesPage /></ModuleGuard>} />
                <Route path="comercial/cuentas-por-cobrar" element={<ModuleGuard module="com_invoices"><AccountsReceivablePage /></ModuleGuard>} />
                <Route path="comercial/reportes" element={<ModuleGuard module="com_reports"><MonthlyReportsPage /></ModuleGuard>} />
                <Route path="leads" element={<ModuleGuard module="leads"><AdminLeads /></ModuleGuard>} />
                <Route path="media" element={<ModuleGuard module="media"><AdminMedia /></ModuleGuard>} />
                <Route path="users" element={<ModuleGuard module="users"><AdminUsers /></ModuleGuard>} />
                <Route path="appearance" element={<ModuleGuard module="appearance"><AdminAppearance /></ModuleGuard>} />
                <Route path="menu-config" element={<ModuleGuard module="settings"><AdminMenuConfig /></ModuleGuard>} />
                <Route path="proposal-themes" element={<ModuleGuard module="proposals"><AdminThemesPage /></ModuleGuard>} />
                <Route path="account" element={<AdminAccount />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </SiteProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
